import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';

// ============================================================
// IMPORTANT: SET UP YOUR FORMSPREE ACCOUNT
// ============================================================
// 1. Go to https://formspree.io and sign up (FREE)
// 2. Create a new form and copy your form ID
// 3. Replace 'YOUR_FORMSPREE_ID' below with your actual ID
//    Example: If your endpoint is https://formspree.io/f/xwkgjqla
//    Then your ID is: xwkgjqla
// ============================================================
const FORMSPREE_ID = 'mlgwdnaw';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: formData.service,
          message: formData.message,
          _subject: `New Quote Request from ${formData.name}`,
        }),
      });

      if (response.ok) {
        toast.success('Quote request sent successfully! We\'ll get back to you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        toast.error('Something went wrong. Please try again or call us directly.');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      content: '07738 965 968',
      link: 'tel:07738965968'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'cpi-leics@outlook.com',
      link: 'mailto:cpi-leics@outlook.com'
    },
    {
      icon: MapPin,
      title: 'Service Area',
      content: 'Based in Burbage, serving Hinckley, South Leicestershire, Nuneaton & the Midlands (LE9, LE10, CV10-13)',
      link: null
    },
    {
      icon: Clock,
      title: 'Hours',
      content: 'Mon-Fri: 8:00 AM - 5:00 PM\nSat: 8:00 AM - 1:00 PM\nSun: Closed',
      link: null
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-[#4ade80]/5 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Get in <span className="text-[#4ade80]">Touch</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Get in touch today to discuss your next project. At Capital Property Improvements, quality, professionalism, and a friendly, client-focused approach are at the heart of everything we do.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <Card
                  key={index}
                  className="bg-[#1a1a1a] border-gray-800 hover:border-[#4ade80] transition-all duration-300 hover-lift"
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-[#4ade80]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-[#4ade80]" size={24} />
                    </div>
                    <h3 className="text-white font-semibold mb-2">{info.title}</h3>
                    {info.link ? (
                      <a
                        href={info.link}
                        className="text-gray-400 hover:text-[#4ade80] transition-colors whitespace-pre-line"
                      >
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-gray-400 whitespace-pre-line">{info.content}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Quote Form */}
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-white mb-6">
                  Request a <span className="text-[#4ade80]">Free Quote</span>
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-300 mb-2 block">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-[#0a0a0a] border-gray-700 text-white focus:border-[#4ade80]"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-300 mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-[#0a0a0a] border-gray-700 text-white focus:border-[#4ade80]"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-gray-300 mb-2 block">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="bg-[#0a0a0a] border-gray-700 text-white focus:border-[#4ade80]"
                      placeholder="07XXX XXXXXX"
                    />
                  </div>

                  <div>
                    <Label htmlFor="service" className="text-gray-300 mb-2 block">
                      Service Required
                    </Label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-3 py-2 bg-[#0a0a0a] border border-gray-700 rounded-md text-white focus:border-[#4ade80] focus:outline-none focus:ring-1 focus:ring-[#4ade80]"
                    >
                      <option value="">Select a service</option>
                      <option value="building">General Building</option>
                      <option value="extension">House Extension</option>
                      <option value="renovation">Renovation & Restoration</option>
                      <option value="conversion">Conversion</option>
                      <option value="roofing">Roofing Services</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-300 mb-2 block">
                      Project Details *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="bg-[#0a0a0a] border-gray-700 text-white focus:border-[#4ade80] resize-none"
                      placeholder="Please provide details about your project..."
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#4ade80] hover:bg-[#22c55e] text-black font-semibold text-lg py-6"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Quote Request
                        <Send className="ml-2" size={18} />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Map and Additional Info */}
            <div className="space-y-6">
              <Card className="bg-[#1a1a1a] border-gray-800">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-white mb-6">
                    Our <span className="text-[#4ade80]">Service Areas</span>
                  </h2>
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    Based in Burbage, we proudly serve Hinckley, South Leicestershire, Nuneaton and the Midlands, covering postcodes LE9, LE10, and CV10-13. With over 20 years of experience and as a CITB-registered company, our local presence means quick response times and professional service you can trust.
                  </p>

                  {/* Interactive Map */}
                  <div className="relative h-96 bg-[#0a0a0a] rounded-lg overflow-hidden border border-gray-800">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d38876.64382968751!2d-1.3909!3d52.5404!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4877611e8c00b7d9%3A0x28d6f3cfeef53a38!2sHinckley%2C%20UK!5e0!3m2!1en!2sus!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Service Area Map"
                    ></iframe>
                  </div>

                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div className="bg-[#0a0a0a] p-4 rounded-lg border border-gray-800">
                      <div className="text-[#4ade80] font-bold text-lg mb-1">LE9 & LE10</div>
                      <div className="text-gray-400 text-sm">Hinckley Area</div>
                    </div>
                    <div className="bg-[#0a0a0a] p-4 rounded-lg border border-gray-800">
                      <div className="text-[#4ade80] font-bold text-lg mb-1">CV10-13</div>
                      <div className="text-gray-400 text-sm">Nuneaton Area</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-[#4ade80]/10 to-transparent border-[#4ade80]/30">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Why Choose Capital Property Improvements?
                  </h3>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start">
                      <div className="w-1.5 h-1.5 bg-[#4ade80] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      Over 20 years of experience
                    </li>
                    <li className="flex items-start">
                      <div className="w-1.5 h-1.5 bg-[#4ade80] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      CITB-registered company
                    </li>
                    <li className="flex items-start">
                      <div className="w-1.5 h-1.5 bg-[#4ade80] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      Based in Burbage, serving the Midlands
                    </li>
                    <li className="flex items-start">
                      <div className="w-1.5 h-1.5 bg-[#4ade80] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      Free, no-obligation quotes
                    </li>
                    <li className="flex items-start">
                      <div className="w-1.5 h-1.5 bg-[#4ade80] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      Quality, professionalism & client-focused approach
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Areas We Cover Section */}
      <section className="section-padding bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              <span className="text-[#4ade80]">Areas</span> We Cover
            </h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto">
              Based in Burbage, we operate across a wide service area covering Hinckley, South Leicestershire, Nuneaton and the Midlands
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="bg-[#0a0a0a] border-gray-800">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#4ade80] mb-3">Hinckley Area (LE9, LE10)</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Hinckley</li>
                  <li>• Burbage</li>
                  <li>• Earl Shilton</li>
                  <li>• Barwell</li>
                  <li>• Market Bosworth</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-[#0a0a0a] border-gray-800">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#4ade80] mb-3">Nuneaton Area (CV10-13)</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Nuneaton</li>
                  <li>• Bedworth</li>
                  <li>• Atherstone</li>
                  <li>• Coleshill</li>
                  <li>• Water Orton</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-[#0a0a0a] border-gray-800">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#4ade80] mb-3">South Leicestershire</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Lutterworth</li>
                  <li>• Broughton Astley</li>
                  <li>• Wigston</li>
                  <li>• Blaby</li>
                  <li>• And surrounding villages</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-400">
              Don't see your area listed? <a href="tel:07738965968" className="text-[#4ade80] hover:text-[#22c55e] font-semibold">Give us a call</a> - we may still be able to help!
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;